import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';

/**
 * Why ops retried — mirrors delivery-service's `RetryReasonCode` (the write is
 * validated against that set, so the two must stay in step). A fixed list
 * rather than the seeded reasons API: the retry is an ops override with
 * exactly these causes, and the code is what makes the timeline filterable.
 */
export const RETRY_REASON_OPTIONS = [
  {
    code: 'DRIVER_MISMARKED_FAILED',
    label: 'Salah tandai gagal oleh driver',
  },
  {
    code: 'CLIENT_REQUESTED_RESEND',
    label: 'Permintaan client — kirim ulang',
  },
  {
    code: 'RECIPIENT_NOW_REACHABLE',
    label: 'Penerima sudah bisa dihubungi',
  },
  { code: 'OTHER', label: 'Lainnya' },
] as const;

export type RetryReasonCode = (typeof RETRY_REASON_OPTIONS)[number]['code'];

interface RetryDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reasonCode: RetryReasonCode, notes: string) => void;
  isLoading?: boolean;
}

const labelCls = 'mb-1 block text-[11px] font-medium text-text-sub-600';

/**
 * Confirm + audit form for the ops retry: delivery goes back to ALLOCATING,
 * and its shipment and outbound request are reset in the same request
 * (delivery-service calls logistic's retry-reset synchronously before it
 * commits). Reason and notes are both required — the service 400s without
 * either.
 */
const RetryDeliveryModal: React.FC<RetryDeliveryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [reasonCode, setReasonCode] = useState<RetryReasonCode | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setReasonCode(null);
      setNotes('');
    }
  }, [isOpen]);

  const canSubmit = reasonCode !== null && notes.trim().length >= 3;

  const handleSubmit = () => {
    if (!reasonCode) return;
    onConfirm(reasonCode, notes.trim());
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-restart-line" />}
      iconTone="primary"
      title="Kirim ulang delivery ini?"
      subtitle="Delivery akan di-reset ke status Allocating dan bisa dialokasikan ulang ke driver. Status shipment dan outbound request yang terhubung ter-reset langsung dalam satu request yang sama. Aksi ini dicatat di timeline (audit)."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Kembali
          </Button>
          <Button
            tone="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Memproses…' : 'Ya, Kirim Ulang'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <p className={labelCls}>
            Alasan <span className="text-(--state-error-base)">*</span>
          </p>
          <div className="space-y-2 ml-1">
            {RETRY_REASON_OPTIONS.map((opt) => (
              <label
                key={opt.code}
                className="flex items-start gap-2.5 cursor-pointer group"
              >
                <input
                  type="radio"
                  name="retry-reason"
                  value={opt.code}
                  checked={reasonCode === opt.code}
                  onChange={() => setReasonCode(opt.code)}
                  className="mt-0.5 accent-accent shrink-0"
                />
                <span className="text-sm text-text-sub-600 group-hover:text-text-strong-950 leading-snug">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className={labelCls}>
            Catatan <span className="text-(--state-error-base)">*</span>
          </p>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Jelaskan alasan kirim ulang…"
            className="resize-none"
          />
          <p className="mt-1 text-right text-[11px] text-text-soft-400">
            {notes.length}/500
          </p>
        </div>
      </div>
    </FormModal>
  );
};

export default RetryDeliveryModal;
